import React from 'react';
import { BadgeCheck, AlertTriangle, ShieldAlert } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from './ui/tooltip';

/**
 * Small badge that surfaces the Google address-validation result persisted on a
 * property (`address_verified` + `address_validation_verdict`).
 *
 * State precedence (mutually exclusive):
 *  1. verdict ends with `_CORRECTED` -> "Corrected" (amber)
 *  2. verified === true             -> "Verified" (green)
 *  3. verified === false            -> "Unverified" (slate)
 *  4. otherwise (no validation data, e.g. manual entry) -> renders nothing
 *
 * Each state exposes a hover/focus tooltip explaining what it means. `compact`
 * renders an icon only for dense layouts such as the list row.
 */
const AddressVerificationBadge = ({ verified, verdict, compact = false }) => {
  const isCorrected =
    typeof verdict === 'string' && verdict.toUpperCase().endsWith('_CORRECTED');

  let state = null;
  if (isCorrected) {
    state = {
      label: 'Corrected',
      description: 'This address has been validated by the ZenRent platform',
      Icon: AlertTriangle,
      classes: 'bg-amber-50 text-amber-700 border-amber-200',
      iconClass: 'text-amber-600',
    };
  } else if (verified === true) {
    state = {
      label: 'Verified',
      description: 'This address has been validated by the ZenRent platform',
      Icon: BadgeCheck,
      classes: 'bg-green-50 text-[#16A34A] border-green-200',
      iconClass: 'text-[#16A34A]',
    };
  } else if (verified === false) {
    state = {
      label: 'Unverified',
      description: 'This address could not be validated by the ZenRent platform',
      Icon: ShieldAlert,
      classes: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]',
      iconClass: 'text-[#64748B]',
    };
  }

  if (!state) return null;

  const { label, description, Icon, classes, iconClass } = state;

  const trigger = compact ? (
    <span
      aria-label={`Address ${label.toLowerCase()}`}
      className="inline-flex items-center"
    >
      <Icon className={`w-3.5 h-3.5 ${iconClass}`} aria-hidden="true" />
    </span>
  ) : (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${classes}`}
    >
      <Icon className={`w-3.5 h-3.5 ${iconClass}`} aria-hidden="true" />
      {label}
    </span>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent>{description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AddressVerificationBadge;
